import types from '../actions/_actionTypes';
import * as time from '../utils/time';
import initialState from './initialState';

/**
 * True when a summary result belongs to a request that a newer one has already
 * replaced. Requests are stamped in `actions/scan.js` because Bedrock latency
 * varies by seconds, so a re-fire (changing Q1 after Q2) can resolve out of
 * order. An unstamped action is always accepted, keeping older fixtures valid.
 */
function isStaleSummary(state, action) {
  // An unstamped action is always accepted, keeping older fixtures valid.
  if (!action.requestId) return false;
  const current = state.aiSummary && state.aiSummary.requestId;
  // A null `current` means the conversation was reset while this request was in
  // flight, so the result belongs to a conversation that no longer exists —
  // stale, NOT current. Accepting it here repopulated the summary the overlay
  // had just cleared, and the member reopened to the previous session's text.
  return action.requestId !== current;
}

const scan = (state = initialState.scans, action) => {
  var newState;

  switch (action.type) {
    case types.RECEIVE_SCAN: {
      const scanKey = action.name.toLowerCase();
      newState = Object.assign({}, state);
      newState.scanData = Object.assign({}, state.scanData);
      // STORY-013 (#713): stamp the fetch time so `shouldFetchScan` can tell
      // fresh results from stale ones, matching the `when: time.timestamp()`
      // convention in barsReducer / symbolReducer / hotlistReducer.
      newState.scanData[scanKey] = Object.assign({}, action.data.data, {
        when: time.timestamp()
      });
      // Clear any recorded error for this scan on successful fetch
      if (state.scanErrors && state.scanErrors[scanKey]) {
        newState.scanErrors = Object.assign({}, state.scanErrors);
        delete newState.scanErrors[scanKey];
      }
      return newState;
    }
    case types.RECEIVE_USER_SCANS:
      return Object.assign({}, state, {
        isLoading: false,
        userScans: action.data.data.scans,
        staticScans: action.data.data.staticScans || []
      });
    case types.RECEIVE_LAST_SCAN:
      return Object.assign({}, state, {
        lastScan: action.data.data.scan
      });
    case types.SAVE_SCAN: {
      const newScans = Object.assign([], state.userScans);
      newScans.push(action.data.data.scan);
      return Object.assign({}, state, {
        userScans: newScans
      });
    }
    case types.DELETE_SCAN:
      return Object.assign({}, state, {
        userScans: state.userScans.filter((scan) => scan.id !== action.id)
      });
    case types.RENAME_SCAN:
      return Object.assign({}, state, {
        userScans: state.userScans.map((s) =>
          s.id === action.data.id ? action.data : s
        ),
        selectedScan:
          state.selectedScan && state.selectedScan.id === action.data.id
            ? action.data
            : state.selectedScan
      });
    case types.ERROR_REQUESTING_SCAN: {
      const scanKey = (action.name || '').toLowerCase();
      newState = Object.assign({}, state);
      if (scanKey) {
        // Store a minimal serializable shape rather than the raw Error
        // instance (keeps Redux state serializable). The consumer only needs
        // the error's presence; message/code are kept for future surfacing.
        newState.scanErrors = Object.assign({}, state.scanErrors || {}, {
          [scanKey]: {
            message: (action.error && action.error.message) || 'Unknown error',
            code: action.errorCode
          }
        });
      }
      return newState;
    }
    case types.ERROR_REQUESTING_USER_SCANS:
      newState = Object.assign({}, state);
      return newState;
    case types.ERROR_REQUESTING_LAST_SCAN:
      newState = Object.assign({}, state);
      return newState;
    case types.ERROR_DELETING_SCAN:
      newState = Object.assign({}, state);
      return newState;
    case types.SET_SELECTED_SCAN:
      return Object.assign({}, state, {
        selectedScan: action.data
      });
    case types.SET_ENABLED_SCAN_CRITERIA: {
      const fieldId = action.fieldId;
      const enabled = action.enabled;

      const nextMap = Object.assign({}, state.enabledScanCriteria);
      if (enabled === false) {
        nextMap[fieldId] = false;
      } else {
        delete nextMap[fieldId];
      }

      return Object.assign({}, state, {
        enabledScanCriteria: nextMap
      });
    }
    case types.RESET_ENABLED_SCAN_CRITERIA:
      return Object.assign({}, state, {
        enabledScanCriteria: {}
      });
    case types.SET_AI_SCANNER_ANSWER:
      return Object.assign({}, state, {
        aiScannerAnswers: Object.assign({}, state.aiScannerAnswers, {
          [action.questionId]: action.code
        })
      });
    case types.RESET_AI_SCANNER_ANSWERS:
      // The overlay closing ends the conversation: answers, the session id, and
      // any summary all belong to that conversation, so they clear together. A
      // reopened overlay starts fresh (STORY-009).
      return Object.assign({}, state, {
        aiScannerAnswers: { q1: null, q2: null },
        aiScannerSessionId: null,
        aiSummary: {
          isLoading: false,
          requestId: null,
          text: null,
          errorCode: null,
          errorMessage: null
        }
      });
    case types.START_AI_SCANNER_SESSION:
      return Object.assign({}, state, {
        aiScannerSessionId: action.sessionId
      });
    case types.REQUESTING_SCAN_SUMMARY:
      // Keep any previous text on screen while a re-request is in flight — a
      // changed Q1 re-fires, and blanking the summary would flash the display.
      return Object.assign({}, state, {
        aiSummary: Object.assign({}, state.aiSummary, {
          isLoading: true,
          requestId: action.requestId,
          errorCode: null,
          errorMessage: null
        })
      });
    case types.RECEIVE_SCAN_SUMMARY:
      // Ignore a result that a newer request has already superseded, so a slow
      // response cannot overwrite the summary for the member's latest answers.
      if (isStaleSummary(state, action)) return state;
      return Object.assign({}, state, {
        aiSummary: {
          isLoading: false,
          requestId: action.requestId,
          text: action.summaryText,
          errorCode: null,
          errorMessage: null
        }
      });
    case types.ERROR_REQUESTING_SCAN_SUMMARY:
      if (isStaleSummary(state, action)) return state;
      // Unlike REQUESTING_SCAN_SUMMARY above, a failure DOES clear the text. A
      // re-request means the member changed an answer, so the previous summary
      // describes a profile they no longer hold — and with no replacement
      // coming, it would sit there mismatched. Absent beats stale here.
      return Object.assign({}, state, {
        aiSummary: {
          isLoading: false,
          requestId: action.requestId,
          text: null,
          errorCode: action.errorCode || null,
          errorMessage: action.errorMessage || null
        }
      });
    default:
      return state;
  }
};

export default scan;
